import { useMemo } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { getSsoSession } from 'auth/ssoClient'
import { useBrand } from 'config/brand'
import { decryptQueryParams } from 'utils/crypto'

/**
 * What a visitor sees with a session but no record: there is nothing to show
 * here on its own. The screens are entered from Kit19 Sales, which sends the
 * record inside the link. The developer test page lives at /testing and is not
 * linked from anywhere.
 *
 * A link that does carry a record opens it instead of this notice, so the app
 * answers the short form too - `https://common.kit19.com/?q=…` - not only
 * `/view?q=…`. The query travels on untouched (encrypted `q=` included), and a
 * caller lookup (`iscomefromcall`) goes to the lookup screen. A visit with
 * nothing in the link still lands here.
 */
const value = (params: Record<string, string>, ...names: string[]): string => {
  const byLowerKey = new Map(Object.entries(params).map(([key, v]) => [key.toLowerCase(), v]))
  for (const name of names) {
    const found = byLowerKey.get(name.toLowerCase())
    if (found !== undefined && found.trim() !== '') return found.trim()
  }
  return ''
}

export default function Landing() {
  useBrand('Entity View / Edit')
  const location = useLocation()
  const params = useMemo(() => decryptQueryParams(location.search), [location.search])

  const fromCall = ['1', 'true', 'yes'].includes(value(params, 'iscomefromcall').toLowerCase())
  const hasRecord = value(params, 'entityID', 'entityId', 'id') !== ''
  const hasNumber = value(params, 'mobileNo', 'mobile', 'mobileNumber', 'phone') !== ''

  if (hasRecord) return <Navigate to={`/view${location.search}`} replace />
  if (fromCall && hasNumber) return <Navigate to={`/view/call${location.search}`} replace />

  const session = getSsoSession()
  return (
    <div className="app-shell">
      <div className="auth-error card">
        <h2>Open a record from the CRM</h2>
        <p>
          This app shows one lead or enquiry at a time. Use <strong>View entity</strong> or{' '}
          <strong>Edit entity</strong> on a record in the CRM and it opens here
          {session.isAuthenticated && session.displayName ? ` as ${session.displayName}` : ''}.
        </p>
      </div>
    </div>
  )
}
